import * as amplitude from "@amplitude/analytics-browser";
import { useApolloClient } from "@apollo/client";
import { Flex, MenuItem, Spinner, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { withdrawFromWaitlist } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import {
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
} from "src/scenes/parent/formTemplates/graphql/queries";
import { useNextStep } from "src/scenes/parent/nextStep/NextStepProvider";
import { refreshFormAdmissionsAfterMutation } from "../../admissions";

interface Props {
  formId: uuid;
  schoolId: uuid;
  school: string;
  grade: string;
  isDisabled?: boolean;
  onActionPendingChange?: (pending: boolean) => void;
}

export const WithdrawForm = ({
  formId,
  schoolId,
  school,
  grade,
  isDisabled = false,
  onActionPendingChange,
}: Props) => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const nextStep = useNextStep();

  const toast = useAvelaToast({ position: "bottom" });

  const [isLoading, setIsLoading] = React.useState(false);

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "No, cancel",
      },
      confirmButton: {
        label: "Yes, withdraw",
        colorScheme: "red",
      },
      translate: true,
    });

  const handleOnClick = async (evt: React.MouseEvent) => {
    setHeader(<Text>Withdraw form?</Text>);
    setBody(
      <Text>{`This will remove your form from ${school} for the ${grade} waitlist.\n\n This can't be undone nor recovered.`}</Text>
    );

    if (await confirm()) {
      setIsLoading(true);
      onActionPendingChange?.(true);
      try {
        const response = await withdrawFromWaitlist({
          path: { formId },
          body: { data: { school_id: schoolId } },
        });

        nextStep.process(response.data?.data.assignedForms);
        apolloClient.refetchQueries({
          include: [GET_PARENT_FORMS_INDEX, GET_PARENT_ASSIGNED_FORMS],
        });

        await refreshFormAdmissionsAfterMutation(queryClient, formId);

        toast({
          title: "Withdrawn from waitlist",
          status: "info",
        });
      } catch (err) {
        toast.error({
          title: "Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
      } finally {
        setIsLoading(false);
        onActionPendingChange?.(false);
      }
    }
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={(e) => {
          amplitude.track("Withdraw from waitlist clicked", {
            button_location: "overflow_menu",
          });
          handleOnClick(e);
        }}
        isDisabled={isDisabled && !isLoading}
      >
        <Flex align="center" gap={2}>
          {isLoading && <Spinner size="xs" flexShrink={0} />}
          Withdraw from waitlist
        </Flex>
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
